import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import Script from 'next/script';

import { useAppSelector } from 'redux/store';
import { FooterFieldsFragment, NavbarFieldsFragment } from 'api/graphql-types';
import { Navbar } from 'modules/navbar/navbar';
import { Footer } from 'modules/footer/footer';
import { SkipLinks } from 'components/skip-links';
import { Note } from 'modules/note/note';
import { Comments } from 'modules/comments/comments';
import { PreviewErrorModal } from 'components/preview-error-modal';

import styles from './layout.module.scss';

interface ILayoutProps {
	navbar?: NavbarFieldsFragment;
	footer?: FooterFieldsFragment;
	typeName?: string;
	children: React.ReactNode;
	customSearch?: (search: string) => void;
	contentId?: string;
}

const Layout = (props: ILayoutProps): React.JSX.Element => {
	const session = useSession();
	const router = useRouter();
	const { hideLanguageSwitcher, hideSearchbar, hideDropdowns, contentTypeId, draftMode } = useAppSelector(
		(state) => state.settings
	);

	const [isCheckingPreview, setIsCheckingPreview] = useState(true);
	const [showPreviewError, setShowPreviewError] = useState(false);

	useEffect(() => {
		const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

		/**
		 * If the page is in draft mode, check if it's being previewed in an iframe (Strapi preview).
		 * If in an iframe, allow the preview to continue.
		 * If NOT in an iframe, someone navigated directly to a draft URL - exit preview mode.
		 */
		if (draftMode) {
			if (isInIframe) {
				setIsCheckingPreview(false);
			} else {
				const exitPreview = async (): Promise<void> => {
					await fetch('/api/exit-preview', { method: 'POST' });
					router.reload();
					setIsCheckingPreview(false);
				};
				exitPreview();
			}
		} else {
			/**
			 * If not in draft mode, check if viewed in an iframe.
			 * If in an iframe without draft mode, show error modal.
			 * If NOT in an iframe, this is a normal page visit - proceed normally.
			 */
			if (isInIframe && router.pathname !== '/404') {
				setShowPreviewError(true);
			}
			setIsCheckingPreview(false);
		}
	}, [draftMode, router]);

	if (draftMode && isCheckingPreview) {
		return <></>;
	}

	return (
		<>
			{typeof window !== 'undefined' &&
				window.self === window.top &&
				window.location.host.includes('strapi.acc') &&
				process.env.NEXT_PUBLIC_USERBACK_API_KEY && (
					<Script id="userback" strategy="afterInteractive">
						{`window.Userback = window.Userback || {};
					Userback.access_token = '${process.env.NEXT_PUBLIC_USERBACK_API_KEY}';
					(function(d) {
						var s = d.createElement('script');s.async = true;
						s.src = 'https://static.userback.io/widget/v1.js';
						(d.head || d.body).appendChild(s);
					})(document);`}
					</Script>
				)}
			<div className={styles.layout}>
				<SkipLinks />
				{props.navbar && (
					<Navbar
						navbar={props.navbar}
						hideSearchbar={hideSearchbar}
						hideLanguageSwitcher={hideLanguageSwitcher}
						hideDropdowns={hideDropdowns}
						customSearch={props.customSearch}
					/>
				)}
				<main className={styles.main} id="content">
					{props.children}
					{contentTypeId && props.typeName && <Comments id={contentTypeId} typeName={props.typeName} />}
				</main>
				{props.footer && <Footer footer={props.footer} />}
			</div>
			{session?.status === 'authenticated' && <Note documentId={contentTypeId} />}
			<PreviewErrorModal isOpen={showPreviewError} />
		</>
	);
};

export default Layout;
